<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯商品' : '新增商品' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="goBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <button
          type="submit"
          form="product-basic-form"
          class="btn btn-primary"
          :disabled="submitting || (loading && isEdit)"
        >
          <i class="ri-save-line"></i>
          {{ submitting ? '存檔中...' : (isEdit ? '存檔' : '建立') }}
        </button>
      </div>
    </div>

    <div class="page-container">

    <div v-if="loading && isEdit" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <template v-else>
      <!-- 頁籤列 -->
      <div class="tab-bar">
        <button
          type="button"
          class="tab-btn"
          :class="{ active: activeTab === 'basic' }"
          @click="activeTab = 'basic'"
        >
          <i class="ri-file-info-line"></i> 基本資料
        </button>
        <button
          v-if="currentProductId"
          type="button"
          class="tab-btn"
          :class="{ active: activeTab === 'variants' }"
          @click="activeTab = 'variants'"
        >
          <i class="ri-list-unordered"></i> 規格
          <span v-if="displayVariants.length" class="tab-badge">{{ displayVariants.length }}</span>
        </button>
        <button
          v-if="currentProductId"
          type="button"
          class="tab-btn"
          :class="{ active: activeTab === 'images' }"
          @click="activeTab = 'images'"
        >
          <i class="ri-image-line"></i> 圖片
          <span v-if="productImages.length" class="tab-badge">{{ productImages.length }}</span>
        </button>
        <button
          v-if="currentProductId && form.productType === 'bundle'"
          type="button"
          class="tab-btn"
          :class="{ active: activeTab === 'bundle' }"
          @click="activeTab = 'bundle'"
        >
          <i class="ri-links-line"></i> 套組內容
          <span v-if="bundleItems.length" class="tab-badge">{{ bundleItems.length }}</span>
        </button>
      </div>

      <!-- ===== 頁籤：基本資料 ===== -->
      <div v-show="activeTab === 'basic'">
        <form id="product-basic-form" class="product-form" @submit.prevent="handleSubmit">
          <div class="form-card">
            <h3 class="form-section-title">基本資料</h3>

            <!-- 商品名稱：單獨一行 -->
            <div class="form-row form-row-block">
              <label class="form-label required">商品名稱</label>
              <input v-model="form.name" type="text" class="form-control" maxlength="100" required placeholder="請輸入商品名稱" />
              <div class="char-count" :class="{ 'char-count-warning': form.name.length >= 90 }">
                {{ form.name.length }} / 100
              </div>
            </div>

            <!-- 商品類型 -->
            <div class="form-row form-row-block">
              <label class="form-label">商品類型</label>
              <div class="product-type-selector">
                <label class="type-option" :class="{ active: form.productType === 'standard' }">
                  <input type="radio" v-model="form.productType" value="standard" />
                  <i class="ri-box-3-line"></i>
                  <span>一般商品</span>
                </label>
                <label class="type-option" :class="{ active: form.productType === 'bundle' }">
                  <input type="radio" v-model="form.productType" value="bundle" />
                  <i class="ri-links-line"></i>
                  <span>套組商品</span>
                </label>
              </div>
              <p v-if="form.productType === 'bundle'" class="field-hint">
                套組商品由多個一般商品組成，下訂時系統會自動扣除各組件的庫存。套組<strong>只允許一種規格</strong>，請至「規格」頁籤設定 SKU 與原價，並至「套組內容」頁籤設定組件。
              </p>
              <p v-else class="field-hint field-hint-info">
                <i class="ri-information-line"></i>
                SKU、條碼、前台顯示、原價、特價、庫存與尺寸重量請在儲存商品後，至「規格」頁籤設定。
              </p>
            </div>

            <div class="form-row form-row-block">
              <label class="form-label">物流溫層</label>
              <div class="status-radio-group">
                <div class="form-check form-check-inline form-radio-secondary">
                  <input id="logisticsTemperatureNormal" v-model="form.logisticsTemperature" class="form-check-input" type="radio" value="normal" />
                  <label class="form-check-label" for="logisticsTemperatureNormal">常溫</label>
                </div>
                <div class="form-check form-check-inline form-radio-secondary">
                  <input id="logisticsTemperatureReefer" v-model="form.logisticsTemperature" class="form-check-input" type="radio" value="reefer" />
                  <label class="form-check-label" for="logisticsTemperatureReefer">冷藏</label>
                </div>
                <div class="form-check form-check-inline form-radio-secondary">
                  <input id="logisticsTemperatureFreeze" v-model="form.logisticsTemperature" class="form-check-input" type="radio" value="freeze" />
                  <label class="form-check-label" for="logisticsTemperatureFreeze">冷凍</label>
                </div>
              </div>
              <p class="field-hint">結帳時同一筆訂單只能有一種溫層。未選時視為常溫。</p>
            </div>

            <div class="form-row form-row-block">
              <label class="form-label">上架狀態</label>
              <div class="status-radio-group">
                <div class="form-check form-check-inline form-radio-secondary">
                  <input id="productStatusActive" v-model="form.status" class="form-check-input" type="radio" value="active" />
                  <label class="form-check-label" for="productStatusActive">上架</label>
                </div>
                <div class="form-check form-check-inline form-radio-secondary">
                  <input id="productStatusInactive" v-model="form.status" class="form-check-input" type="radio" value="inactive" />
                  <label class="form-check-label" for="productStatusInactive">下架</label>
                </div>
              </div>
              <p class="field-hint">下架後，前台列表、商品頁與結帳都不會出現這個商品。</p>
            </div>

            <!-- 商品分類：一階 + 二階 + 三階 -->
            <div class="form-row form-row-block">
              <div class="form-row-inline">
                <div class="form-inline-item">
                  <label class="form-label">商品分類（一階）</label>
                  <select v-model="selectedL1Id" class="form-control" @change="onL1CategoryChange">
                    <option :value="null">請選擇分類</option>
                    <option v-for="cat in categoriesTree" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                  </select>
                </div>
                <div class="form-inline-item">
                  <label class="form-label">子分類（二階）</label>
                  <select v-model="selectedL2Id" class="form-control" :disabled="!selectedL1Id || !l2Categories.length" @change="onL2CategoryChange">
                    <option :value="null">（無，使用一階分類）</option>
                    <option v-for="child in l2Categories" :key="child.id" :value="child.id">{{ child.name }}</option>
                  </select>
                </div>
                <div v-if="l3Categories.length" class="form-inline-item">
                  <label class="form-label">子分類（三階）</label>
                  <select v-model="form.categoryId" class="form-control">
                    <option :value="selectedL2Id">（無，使用二階分類）</option>
                    <option v-for="gc in l3Categories" :key="gc.id" :value="gc.id">{{ gc.name }}</option>
                  </select>
                </div>
              </div>
              <div v-if="!categoriesTree.length" class="field-hint">
                尚未建立分類，請前往
                <router-link to="/client/products/categories">商品分類管理</router-link>
                新增分類。
              </div>
            </div>

            <!-- 規格屬性（僅一般商品） -->
            <div v-if="form.productType === 'standard'" class="form-row form-row-block">
              <label class="form-label">規格屬性</label>
              <p class="field-hint">
                勾選此商品會用到的屬性，建立規格時僅顯示已勾選項目，且每種規格皆須填寫。未勾選時只能建立一種規格。最多可選 {{ MAX_ATTRIBUTE_DEFINITIONS }} 個。
                <span class="attribute-selection-count">（已選 {{ form.attributeDefinitionIds.length }} / {{ MAX_ATTRIBUTE_DEFINITIONS }}）</span>
              </p>
              <div v-if="!attributeDefinitions.length" class="field-hint field-hint-info">
                <i class="ri-information-line"></i>
                尚未建立屬性，請前往
                <router-link to="/client/products/attributes">商品屬性定義</router-link>
                新增後再回來勾選。
              </div>
              <div v-else class="attribute-checkbox-list">
                <label
                  v-for="def in attributeDefinitions"
                  :key="def.id"
                  class="attribute-checkbox-item"
                  :class="{ 'attribute-checkbox-item--disabled': isAttributeCheckboxDisabled(def.id) }"
                >
                  <input
                    v-model="form.attributeDefinitionIds"
                    type="checkbox"
                    :value="def.id"
                    :disabled="isAttributeCheckboxDisabled(def.id)"
                  />
                  <span class="attribute-checkbox-label">
                    {{ def.attributeName }}
                    <span class="attr-code-hint">{{ def.attributeCode }}</span>
                  </span>
                </label>
              </div>
            </div>

            <div class="form-row form-row-block">
              <label class="form-label">商品簡述</label>
              <textarea v-model="form.summary" class="form-control" rows="3" placeholder="選填"></textarea>
            </div>

            <div class="form-row form-row-block">
              <label class="form-label">內容簡介</label>
              <div class="quill-wrapper">
                <quillEditor
                  :value="form.introduction"
                  @update:value="form.introduction = $event"
                  :options="quillOptions"
                  class="quill-editor"
                />
              </div>
            </div>

            <div class="form-row form-row-block">
              <label class="form-label">規格說明</label>
              <div class="quill-wrapper">
                <quillEditor
                  :value="form.specDescription"
                  @update:value="form.specDescription = $event"
                  :options="quillOptions"
                  class="quill-editor"
                />
              </div>
            </div>

            <div class="form-row form-row-block">
              <label class="form-label">退貨說明</label>
              <div class="quill-wrapper">
                <quillEditor
                  :value="form.returnDescription"
                  @update:value="form.returnDescription = $event"
                  :options="quillOptions"
                  class="quill-editor"
                />
              </div>
            </div>
          </div>

          <div class="form-card">
            <h3 class="form-section-title">SEO 與前台設定</h3>
            <FormTipBox spacing="section">
              <p class="seo-help-intro">
                <strong>整體說明：</strong>此區塊影響<strong>前台商品詳情頁網址</strong>與<strong>搜尋／分享預覽</strong>使用的文字。
                <strong>URL Slug</strong>決定商品頁路徑；<strong>SEO 標題／描述</strong>對應搜尋結果與瀏覽器分頁、摘要（與 CMS 單頁設定概念相同）；<strong>SEO 關鍵字</strong>寫入商品主檔，供 meta keywords 或站內用途參考。
              </p>
            </FormTipBox>
            <div class="form-row form-row-block">
              <label class="form-label">前台 URL Slug</label>
              <input v-model="form.storefrontSlug" class="form-control" placeholder="留空則儲存後寫入商品 ID" />
              <FormTipBox spacing="compact">
                <p class="field-hint">
                  <strong>用途：</strong>作為前台<strong>商品詳情頁網址</strong>中的一段（與公開 API、前台路由使用的 <code>storefront_slug</code> 相同）。
                  同一商店內不可與<strong>其他商品</strong>的 Slug 重複。
                </p>
                <p class="field-hint">
                  <strong>留空時：</strong>儲存後寫入這個商品的 ID。新商品要等儲存後才看得到。
                  清空已填的 Slug 再儲存，網址會改成商品 ID，舊網址會失效。
                </p>
                <p class="field-hint">
                  <strong>自行填寫：</strong>只能使用英數字與連字號 <code>-</code>。空白會併成連字號，英文會轉成小寫。
                  含其他字元時無法儲存；與其他商品重複時也無法儲存。
                </p>
                <p class="field-hint field-hint-example">
                  <strong>設定範例：</strong><span class="seo-example-text">running-shoe-2025</span>
                </p>
                <p class="field-hint field-hint-preview-path">
                  <strong>前台路徑預覽：</strong>
                  <code v-if="previewProductStorefrontSlug" class="seo-path-code">/p/{{ previewProductStorefrontSlug }}</code>
                  <span v-else>只能使用英數字與連字號</span>
                  <span class="field-hint-sub">示範為常見商品頁前綴 <code>/p/</code>；完整網址 = 商店前台網域 + 上列路徑（實際路由以前台專案為準）。</span>
                </p>
              </FormTipBox>
            </div>
            <CommerceSeoMetaFields
              v-model:seo-title="form.seoTitle"
              v-model:seo-description="form.seoDescription"
              v-model:seo-keywords="form.seoKeywords"
              variant="product"
              embedded
            />

          </div>

        </form>
      </div>

      <!-- ===== 頁籤：規格 ===== -->
      <div v-if="currentProductId" v-show="activeTab === 'variants'">
        <!-- 分倉庫存管理提示 -->
        <div v-if="inventoryManaged" class="inventory-managed-banner">
          <i class="ri-information-line"></i>
          已啟用
          <strong>
            {{
              inventorySettings.order_deduct_mode === 'specific_warehouse' ? '指定倉庫' :
              inventorySettings.order_deduct_mode === 'store_warehouse' ? '依門市倉庫' :
              '所有倉庫加總'
            }}
          </strong>
          扣減模式，庫存數量由倉庫管理，規格庫存欄位僅供參考，請至
          <router-link to="/client/inventory/overview" class="banner-link">庫存總覽</router-link>
          調整各倉庫存量。
        </div>

        <div class="form-card">
          <div v-if="form.productType === 'bundle' && displayVariants.length >= 1 && !showVariantForm" class="bundle-variant-limit-hint">
            <i class="ri-information-line"></i>
            套組商品只允許一種規格，如需調整請直接編輯下方規格。
          </div>
          <div v-if="form.productType === 'standard' && !productVariantAttributeDefinitions.length && displayVariants.length >= 1 && !showVariantForm" class="bundle-variant-limit-hint">
            <i class="ri-information-line"></i>
            未勾選規格屬性時，商品只允許一種規格，如需調整請直接編輯下方規格。若要新增多種規格，請至「基本資料」勾選規格屬性。
          </div>
          <div class="variant-toolbar">
            <button
              type="button"
              class="btn btn-primary btn-sm"
              @click="showVariantForm = true"
              v-if="!showVariantForm && canAddVariant"
            >
              <i class="ri-add-line"></i> 新增規格
            </button>
          </div>
          <div v-if="showVariantForm" class="variant-form-inline">
            <div class="form-row">
              <label class="form-label required">SKU</label>
              <input v-model="variantForm.sku" type="text" class="form-control" placeholder="必填" />
              <p class="field-hint">此規格的唯一識別碼，用於庫存追蹤與出貨。</p>
            </div>
            <div class="form-row">
              <label class="form-label">條碼</label>
              <input v-model="variantForm.barcode" type="text" class="form-control" />
            </div>
            <div class="form-row">
              <label class="form-label">前台顯示</label>
              <div class="status-radio-group">
                <div class="form-check form-check-inline form-radio-secondary">
                  <input id="variantVisibleYes" v-model="variantForm.visibleOnStorefront" class="form-check-input" type="radio" :value="true" />
                  <label class="form-check-label" for="variantVisibleYes">顯示</label>
                </div>
                <div class="form-check form-check-inline form-radio-secondary">
                  <input id="variantVisibleNo" v-model="variantForm.visibleOnStorefront" class="form-check-input" type="radio" :value="false" />
                  <label class="form-check-label" for="variantVisibleNo">不顯示</label>
                </div>
              </div>
              <p class="field-hint">不顯示的規格不會出現在前台列表，也不能在前台結帳。後台庫存與訂單仍可使用。</p>
            </div>
            <div class="form-row">
              <label class="form-label required">原價 (NT$)</label>
              <input v-model.number="variantForm.unitPrice" type="number" class="form-control" min="0" step="1" placeholder="必填" />
              <p class="field-hint">有填特價時，下單以特價為準；未填則以原價為準。</p>
            </div>
            <div class="form-row">
              <label class="form-label">特價 (NT$)</label>
              <input v-model.number="variantForm.specialPrice" type="number" class="form-control" min="1" step="1" placeholder="選填，須小於原價" />
            </div>
            <div class="form-row">
              <label class="form-label">
                庫存數量
                <span v-if="inventoryManaged" class="field-managed-badge">
                  <i class="ri-lock-line"></i> 由倉庫管理
                </span>
              </label>
              <!-- 分倉管理模式：只顯示倉庫庫存加總，不允許輸入 -->
              <div v-if="inventoryManaged" class="stock-locked-display">
                <div class="stock-locked-row">
                  <!-- 倉庫加總（即時計算值） -->
                  <template v-if="editingVariantId && variantStockTotals[editingVariantId]">
                    <span class="stock-locked-value">
                      {{ variantStockTotals[editingVariantId].totalQuantity }}
                    </span>
                    <span class="stock-locked-meta">
                      可用 {{ variantStockTotals[editingVariantId].totalAvailable }}
                      ／預留 {{ variantStockTotals[editingVariantId].totalReserved }}
                    </span>
                  </template>
                  <!-- 新增規格 or 尚未查詢到時：顯示原始值 -->
                  <span v-else class="stock-locked-value">{{ variantForm.stockQuantity ?? 0 }}</span>
                  <button
                    v-if="editingVariantId"
                    type="button"
                    class="btn-refresh-stock"
                    title="重新計算倉庫庫存"
                    @click="loadVariantStockTotals"
                  >
                    <i class="ri-refresh-line"></i>
                  </button>
                </div>
                <small class="field-hint-warn">
                  <i class="ri-error-warning-line"></i>
                  <template v-if="inventorySettings.order_deduct_mode === 'store_warehouse'">
                    顯示為<strong>所有倉庫加總</strong>，實際扣減依訂單門市對應倉庫決定，請至
                    <router-link to="/client/inventory/overview" class="hint-link">庫存管理</router-link>
                    查看各倉庫庫存
                  </template>
                  <template v-else>
                    庫存數量由倉庫管理，請至
                    <router-link to="/client/inventory/overview" class="hint-link">庫存管理</router-link>
                    調整
                  </template>
                </small>
              </div>
              <!-- 一般模式：可輸入 -->
              <input
                v-else
                v-model.number="variantForm.stockQuantity"
                type="number"
                class="form-control"
                min="0"
                placeholder="0"
              />
            </div>
            <div class="form-row">
              <label class="form-label">安全庫存（選填）</label>
              <input v-model.number="variantForm.safetyStockQuantity" type="number" class="form-control" min="0" placeholder="低於此數量可提醒" />
            </div>
            <div v-for="def in productVariantAttributeDefinitions" :key="def.id" class="form-row">
              <label class="form-label required">{{ def.attributeName }}</label>
              <select v-model="variantForm.attributes[def.attributeCode]" class="form-control">
                <option value="">請選擇</option>
                <option v-for="opt in (def.options || [])" :key="opt.id" :value="opt.value">{{ opt.value }}</option>
              </select>
            </div>
            <div class="form-row variant-dimensions-section">
              <label class="form-label">尺寸與重量（選填）</label>
              <div class="form-row grid-3">
                <div>
                  <label class="form-label form-label-sub">長 (cm)</label>
                  <input v-model.number="variantForm.lengthCm" type="number" class="form-control" min="0" step="0.01" placeholder="選填" />
                </div>
                <div>
                  <label class="form-label form-label-sub">寬 (cm)</label>
                  <input v-model.number="variantForm.widthCm" type="number" class="form-control" min="0" step="0.01" placeholder="選填" />
                </div>
                <div>
                  <label class="form-label form-label-sub">高 (cm)</label>
                  <input v-model.number="variantForm.heightCm" type="number" class="form-control" min="0" step="0.01" placeholder="選填" />
                </div>
              </div>
              <div class="form-row" style="margin-top: 12px; margin-bottom: 0;">
                <label class="form-label form-label-sub">重量 (kg)</label>
                <input v-model.number="variantForm.weightKg" type="number" class="form-control" min="0" step="0.01" placeholder="選填" />
              </div>
            </div>
            <div class="form-row actions">
              <button type="button" class="btn btn-secondary btn-sm" @click="cancelVariantForm">取消</button>
              <button type="button" class="btn btn-primary btn-sm" @click="submitVariantForm" :disabled="variantSubmitting">
                {{ editingVariantId ? '儲存' : '新增' }}
              </button>
            </div>
          </div>
          <!-- 無規格時的說明 -->
          <div v-if="form.productType === 'standard' && !displayVariants.length && !showVariantForm" class="no-variant-inventory-hint">
            <i class="ri-information-line"></i>
            <div>
              <strong>此商品尚無規格。</strong><br>
              <template v-if="productVariantAttributeDefinitions.length">
                請點「新增規格」設定 SKU、原價等資訊，並填寫所有已勾選的規格屬性。
              </template>
              <template v-else>
                未勾選規格屬性時只能建立一種規格。請點「新增規格」設定 SKU、條碼、原價與庫存；若要區分尺寸、顏色等，請至「基本資料」勾選規格屬性後再新增多筆規格。
              </template>
            </div>
          </div>
          <div v-if="form.productType === 'bundle' && !displayVariants.length && !showVariantForm" class="no-variant-inventory-hint">
            <i class="ri-information-line"></i>
            <div>
              <strong>此商品尚無規格。</strong><br>
              套組商品只允許一種規格，請點「新增規格」設定 SKU、條碼、前台顯示、原價、庫存與尺寸重量。
            </div>
          </div>

          <div v-if="form.productType === 'bundle' && displayVariants.length" class="bundle-stock-hint">
            <i class="ri-information-line"></i>
            套組庫存依各組件可組套數的最小值計算（floor(組件庫存 ÷ 組件數量)）。
            <template v-if="bundleAvailableStock != null">目前可組套數：<strong>{{ bundleAvailableStock }}</strong></template>
          </div>

          <div v-if="!editingVariantId || tableVariants.length" class="table-wrapper variant-table">
            <table>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>條碼</th>
                  <th>原價</th>
                  <th>特價</th>
                  <th>
                    庫存
                    <span v-if="inventoryManaged" class="th-managed-badge" title="庫存由倉庫管理">
                      <i class="ri-store-3-line"></i>
                    </span>
                  </th>
                  <th>安全庫存</th>
                  <th>屬性</th>
                  <th>前台顯示</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!displayVariants.length">
                  <td colspan="9" class="empty-state">尚無規格，可點「新增規格」</td>
                </tr>
                <tr v-for="v in tableVariants" :key="v.id">
                  <td>
                    {{ v.sku }}
                    <span v-if="v.isDefaultVariant" class="default-variant-badge">預設</span>
                  </td>
                  <td>{{ v.barcode || '-' }}</td>
                  <td>NT$ {{ formatNum(v.unitPrice) }}</td>
                  <td>{{ v.specialPrice != null && v.specialPrice !== '' ? `NT$ ${formatNum(v.specialPrice)}` : '-' }}</td>
                  <td>{{ formatVariantStock(v) }}</td>
                  <td>{{ v.safetyStockQuantity ?? '-' }}</td>
                  <td>{{ formatAttributes(v.attributes) }}</td>
                  <td>{{ variantShownOnStorefront(v) ? '顯示' : '不顯示' }}</td>
                  <td>
                    <button type="button" class="btn btn-sm btn-secondary" @click="editVariant(v)" title="編輯"><i class="ri-edit-line"></i></button>
                    <button type="button" class="btn btn-sm btn-danger ms-1" @click="deleteVariantRow(v)" title="刪除"><i class="ri-delete-bin-line"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ===== 頁籤：圖片 ===== -->
      <div v-if="currentProductId" v-show="activeTab === 'images'">
        <div class="form-card">

          <!-- 當前主圖預覽 -->
          <div v-if="form.imageUrl" class="main-image-banner">
            <img :src="form.imageUrl" alt="主圖" class="main-image-banner-thumb" @error="onImgError" />
            <div class="main-image-banner-info">
              <span class="main-image-badge"><i class="ri-star-fill"></i> 目前主圖</span>
              <p class="main-image-note">如需更換主圖，請點選下方圖片的星星。</p>
            </div>
          </div>
          <div v-else class="no-main-image-hint">
            <i class="ri-image-line"></i> 尚未設定主圖，可從下方圖片中點「設為主圖」
          </div>

          <FormTipBox title="圖片建議" spacing="section">
            <p class="field-hint"><strong>比例：</strong>建議 1:1 正方形(如 800×800px)。其他比例可能被裁切。</p>
            <p class="field-hint"><strong>格式：</strong>WebP。</p>
            <p class="field-hint"><strong>大小：</strong>單張不超過 2MB。</p>
            <p class="field-hint"><strong>數量：</strong>最多 6 張。</p>
          </FormTipBox>

          <div class="image-toolbar">
            <div class="add-image-inline">
              <label class="btn btn-secondary image-toolbar-btn btn-upload-multi" :class="{ uploading: batchImageUploading }">
                <i class="ri-upload-line"></i>
                {{ batchImageUploading ? `上傳中 (${batchImageProgress})...` : '上傳多張圖檔' }}
                <input type="file" multiple accept="image/webp" :disabled="imageGalleryFull || batchImageUploading" @change="handleMultipleImageUpload" class="sr-only" />
              </label>
              <button type="button" class="btn btn-primary image-toolbar-btn" @click="addImage" :disabled="!newImageUrl || imageSubmitting || imageGalleryFull">
                {{ imageSubmitting ? '新增中...' : '新增圖片' }}
              </button>
              <input v-model="newImageUrl" type="url" class="form-control image-url-input" placeholder="圖片網址" />
              <select v-model="newImageVariantId" class="form-control image-variant-select">
                <option value="">共用圖</option>
                <option v-for="v in realVariants" :key="v.id" :value="v.id">{{ v.sku }}</option>
              </select>
            </div>
          </div>

          <div class="image-list">
            <div v-for="img in productImages" :key="img.id" class="image-item" :class="{ 'is-main': img.imageUrl === form.imageUrl }">
              <div class="image-item-thumb-wrap">
                <img :src="img.imageUrl" alt="" class="img-thumb" @error="onImgError" />
                <span v-if="img.imageUrl === form.imageUrl" class="img-main-badge"><i class="ri-star-fill"></i></span>
              </div>
              <span class="img-meta">{{ img.variantId ? (getVariantSku(img.variantId) || img.variantId) : '共用' }}</span>
              <div class="img-actions">
                <button
                  v-if="img.imageUrl !== form.imageUrl"
                  type="button"
                  class="btn btn-sm btn-secondary"
                  @click="setMainImage(img)"
                  :disabled="settingMainImage"
                  title="設為主圖"
                >
                  <i class="ri-star-line"></i>
                </button>
                <button type="button" class="btn btn-sm btn-danger" @click="deleteImage(img)" title="刪除">
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
            <div v-if="!productImages.length" class="empty-state">尚無圖片，請上傳或貼上網址</div>
          </div>
        </div>
      </div>

      <!-- ===== 頁籤：套組內容 ===== -->
      <div v-if="currentProductId && form.productType === 'bundle'" v-show="activeTab === 'bundle'">
        <div class="form-card">
          <div class="bundle-header">
            <h3 class="form-section-title">套組組件</h3>
            <p class="bundle-hint">設定套組包含的商品與數量，下訂時系統將自動扣除各組件的庫存。套組可售數量為各組件 floor(庫存 ÷ 數量) 的最小值。</p>
          </div>

          <!-- 新增組件按鈕 -->
          <div class="bundle-add-row">
            <button type="button" class="btn btn-primary btn-sm" @click="showBundlePicker = true">
              <i class="ri-add-line"></i> 新增組件商品
            </button>
          </div>

          <!-- 套組組件列表 -->
          <div v-if="bundleItems.length" class="bundle-items-list">
            <table class="bundle-table">
              <thead>
                <tr>
                  <th>組件商品</th>
                  <th>SKU</th>
                  <th>規格</th>
                  <th style="width: 110px; text-align: center;">數量</th>
                  <th style="width: 60px;"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in bundleItems" :key="index">
                  <td>{{ item.componentProductName }}</td>
                  <td class="sku-cell">{{ formatBundleItemSku(item) }}</td>
                  <td>
                    <template v-if="item.componentVariantAttributes && Object.keys(item.componentVariantAttributes).length">
                      <span class="variant-attrs">{{ Object.values(item.componentVariantAttributes).filter(Boolean).join(' / ') }}</span>
                    </template>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td style="text-align: center;">
                    <input
                      v-model.number="item.quantity"
                      type="number"
                      class="form-control qty-input"
                      min="1"
                    />
                  </td>
                  <td>
                    <button type="button" class="btn btn-sm btn-danger" @click="removeBundleItem(index)" title="移除">
                      <i class="ri-delete-bin-line"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="empty-state">尚未設定組件，請點「新增組件商品」</div>

          <div class="form-actions">
            <button type="button" class="btn btn-primary" @click="saveBundleItems" :disabled="bundleSubmitting">
              {{ bundleSubmitting ? '儲存中...' : '儲存套組內容' }}
            </button>
          </div>
        </div>
      </div>

      <!-- 套組商品選擇器 Modal -->
      <ProductPickerModal
        :open="showBundlePicker"
        title="選擇組件商品"
        :variantRequired="true"
        :excludeProductTypes="['bundle']"
        @close="showBundlePicker = false"
        @select="onBundleItemSelect"
      />
    </template>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { quillEditor } from 'vue3-quill';
import { productService } from '@/api/modules/products.js';
import { showSuccess, showError, showConfirm } from '@/utils/swal.js';
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings.js';
import { warehouseService } from '@/api/modules/warehouses.js';
import { useMenuStore } from '@/stores/menu.js';
import ProductPickerModal from '@/components/ProductPickerModal.vue';
import FormTipBox from '@/components/form/FormTipBox.vue';
import CommerceSeoMetaFields from '@/components/commerce/CommerceSeoMetaFields.vue';

const defaultForm = () => ({
  name: '',
  summary: '',
  introduction: '',
  specDescription: '',
  returnDescription: '',
  productType: 'standard',
  logisticsTemperature: 'normal',
  attributeDefinitionIds: [],
  imageUrl: '',
  categoryId: null,
  storefrontSlug: '',
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
  status: 'active',
});

const MAX_ATTRIBUTE_DEFINITIONS = 3;

const defaultVariantForm = () => ({
  sku: '',
  barcode: '',
  unitPrice: null,
  specialPrice: null,
  stockQuantity: 0,
  safetyStockQuantity: null,
  lengthCm: null,
  widthCm: null,
  heightCm: null,
  weightKg: null,
  attributes: {},
  visibleOnStorefront: true,
  status: 'active',
});

export default {
  name: 'ClientProductForm',
  components: { quillEditor, ProductPickerModal, FormTipBox, CommerceSeoMetaFields },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const isEdit = computed(() => !!route.params.id);
    const routeProductId = computed(() => route.params.id);
    const currentProductId = ref(route.params.id || null);
    const loading = ref(false);
    const submitting = ref(false);
    const activeTab = ref('basic');
    const form = reactive(defaultForm());
    const variants = ref([]);
    const productImages = ref([]);
    const attributeDefinitions = ref([]);
    const showVariantForm = ref(false);
    const variantForm = reactive(defaultVariantForm());
    const variantSubmitting = ref(false);
    const editingVariantId = ref(null);
    const newImageUrl = ref('');
    const newImageVariantId = ref('');
    const imageSubmitting = ref(false);
    const mainImageUploading = ref(false);
    const batchImageUploading = ref(false);
    const batchImageProgress = ref('');
    const settingMainImage = ref(false);
    const categoriesTree = ref([]);
    const selectedL1Id = ref(null);
    const selectedL2Id = ref(null);

    // ── 無規格商品庫存（預設規格）────────────────────────────
    const defaultStockQuantity = ref(null);
    const defaultStockSubmitting = ref(false);

    const realVariants = computed(() => variants.value.filter(v => !v.isDefaultVariant));

    /** 列表顯示：有真實規格時顯示真實規格；僅有預設規格時仍顯示，方便編輯 SKU/原價/庫存 */
    const displayVariants = computed(() => {
      const real = realVariants.value;
      if (real.length > 0) return real;
      return variants.value.filter(v => v.isDefaultVariant);
    });

    /** 編輯中的規格已出現在上方表單，列表不再重複顯示 */
    const tableVariants = computed(() =>
      displayVariants.value.filter((variant) => variant.id !== editingVariantId.value)
    );

    /** 套組或未勾選規格屬性時只允許一種規格；有勾選屬性時可新增多個 */
    const canAddVariant = computed(() => {
      if (form.productType === 'bundle' && displayVariants.value.length >= 1) return false;
      if (form.productType === 'standard' && !productVariantAttributeDefinitions.value.length && displayVariants.value.length >= 1) {
        return false;
      }
      return true;
    });

    /** 基本資料勾選的屬性，供規格表單使用（僅 select 且有選項） */
    const productVariantAttributeDefinitions = computed(() => {
      if (form.productType === 'bundle') return [];
      const selected = new Set(form.attributeDefinitionIds || []);
      return attributeDefinitions.value.filter(
        (d) => selected.has(d.id) && d.valueType === 'select' && (d.options || []).length
      );
    });

    const defaultVariantId = computed(() => variants.value.find(v => v.isDefaultVariant)?.id ?? null);

    // 進銷存模式下，預設規格的倉庫庫存加總（從 variantStockTotals 取出）
    const defaultVariantStockInfo = computed(() =>
      defaultVariantId.value ? (variantStockTotals.value[defaultVariantId.value] ?? null) : null
    );

    const loadDefaultStock = async () => {
      if (!currentProductId.value) return;
      try {
        const res = await productService.getDefaultStock(currentProductId.value);
        defaultStockQuantity.value = (res && res.success && res.data && res.data.stockQuantity != null)
          ? Number(res.data.stockQuantity)
          : null;
      } catch {
        defaultStockQuantity.value = null;
      }
    };

    const saveDefaultStock = async () => {
      if (!currentProductId.value) return;
      defaultStockSubmitting.value = true;
      try {
        await productService.setDefaultStock(currentProductId.value, defaultStockQuantity.value);
      } catch (err) {
        await showError('庫存儲存失敗', err.response?.data?.message || err.message);
      } finally {
        defaultStockSubmitting.value = false;
      }
    };
    // ────────────────────────────────────────────────────────

    // ── 套組商品 ─────────────────────────────────────────────
    const bundleItems = ref([]);
    const bundleAvailableStock = ref(null);
    const bundleSubmitting = ref(false);
    const showBundlePicker = ref(false);

    const loadBundleItems = async () => {
      if (!currentProductId.value || form.productType !== 'bundle') return;
      try {
        const res = await productService.getBundleItems(currentProductId.value);
        if (res && res.success && res.data) {
          bundleItems.value = res.data.items ?? [];
          bundleAvailableStock.value = res.data.availableStock ?? 0;
        } else {
          bundleItems.value = [];
          bundleAvailableStock.value = 0;
        }
      } catch (e) {
        console.error(e);
      }
    };

    const onBundleItemSelect = ({ product, variant }) => {
      const componentVariantId = variant?.id || null;
      const componentVariantSku = variant?.sku || null;
      const componentVariantAttributes = variant?.attributes || null;

      // 檢查是否已存在相同 variant（同商品無規格時以 productId 去重）
      const isDuplicate = bundleItems.value.some(b =>
        componentVariantId
          ? b.componentVariantId === componentVariantId
          : (!b.componentVariantId && b.componentProductId === product.id)
      );
      if (isDuplicate) {
        showError('已存在', '此規格已加入套組，請直接修改數量');
        return;
      }

      bundleItems.value.push({
        componentProductId: product.id,
        componentProductName: product.name,
        componentProductSku: componentVariantSku || product.sku || null,
        componentVariantId,
        componentVariantSku,
        componentVariantAttributes,
        quantity: 1,
        sortOrder: bundleItems.value.length,
      });
    };

    const formatBundleItemSku = (item) => item?.componentVariantSku || item?.componentProductSku || '-';

    const removeBundleItem = (index) => {
      bundleItems.value.splice(index, 1);
      bundleItems.value.forEach((item, i) => { item.sortOrder = i; });
    };

    const saveBundleItems = async () => {
      if (!currentProductId.value) return;
      bundleSubmitting.value = true;
      try {
        await productService.updateBundleItems(
          currentProductId.value,
          bundleItems.value.map((item, i) => ({
            componentProductId: item.componentProductId,
            componentVariantId: item.componentVariantId || null,
            quantity: Number(item.quantity) || 1,
            sortOrder: i,
          }))
        );
        await showSuccess('套組內容已儲存');
        await loadBundleItems();
      } catch (err) {
        await showError('儲存失敗', err.response?.data?.message || err.message);
      } finally {
        bundleSubmitting.value = false;
      }
    };
    // ────────────────────────────────────────────────────────

    // ── 進銷存設定 ───────────────────────────────────────────
    const menuStore = useMenuStore();
    const inventorySettings = ref({ order_deduct_mode: 'disabled' });

    // 進銷存模組是否已啟用
    const inventoryEnabled = computed(() =>
      menuStore.menuItems.some(m => m.code === 'INVENTORY')
    );

    // 啟用分倉扣減時，stockQuantity 改由倉庫管理（唯讀）
    const inventoryManaged = computed(() =>
      inventoryEnabled.value &&
      inventorySettings.value.order_deduct_mode !== 'disabled'
    );

    // variantId -> { totalQuantity, totalReserved, totalAvailable }
    const variantStockTotals = ref({});

    const loadInventorySettings = async () => {
      if (!inventoryEnabled.value) return;
      try {
        const res = await clientSettingsService.get(NAMESPACES.INVENTORY);
        inventorySettings.value = res?.data?.settings ?? { order_deduct_mode: 'disabled' };
      } catch {
        // 讀不到設定，維持預設（不扣減）
      }
    };

    /** 當進銷存為管理模式時，查詢各規格的倉庫庫存加總 */
    const loadVariantStockTotals = async () => {
      if (!inventoryManaged.value || !currentProductId.value) return;
      if (form.productType === 'bundle') {
        await loadBundleItems();
        return;
      }
      try {
        const mode = inventorySettings.value.order_deduct_mode;
        const warehouseId = mode === 'specific_warehouse'
          ? (inventorySettings.value.default_warehouse_id ?? null)
          : null;
        const res = await warehouseService.getProductStockTotals(currentProductId.value, warehouseId);
        const map = {};
        if (res?.data && Array.isArray(res.data)) {
          res.data.forEach(item => { map[item.variantId] = item; });
        }
        variantStockTotals.value = map;
      } catch {
        // 查詢失敗不影響主要功能
      }
    };

    // ── 輪詢：規格面板開啟時，自動同步倉庫庫存數量 ─────────────
    const STOCK_POLL_INTERVAL_MS = 30000;
    let stockPollTimer = null;

    const startStockPolling = () => {
      stopStockPolling();
      if (!inventoryManaged.value) return;
      stockPollTimer = setInterval(() => {
        loadVariantStockTotals();
      }, STOCK_POLL_INTERVAL_MS);
    };

    const stopStockPolling = () => {
      if (stockPollTimer) {
        clearInterval(stockPollTimer);
        stockPollTimer = null;
      }
    };

    // 開啟規格面板且為管理模式 → 啟動輪詢；關閉或切換規格 → 重置計時
    watch(
      [showVariantForm, editingVariantId],
      ([formVisible, variantId]) => {
        if (formVisible && variantId && inventoryManaged.value) {
          startStockPolling();
        } else {
          stopStockPolling();
        }
      }
    );

    onUnmounted(stopStockPolling);

    watch(activeTab, (tab) => {
      if (tab === 'variants' && currentProductId.value) {
        if (form.productType === 'bundle') {
          loadBundleItems();
        } else if (inventoryManaged.value) {
          loadVariantStockTotals();
        }
      }
    });

    watch(
      () => form.productType,
      (type) => {
        if (type === 'bundle') {
          form.attributeDefinitionIds = [];
          syncVariantFormAttributes();
        }
      }
    );

    watch(
      () => [...(form.attributeDefinitionIds || [])],
      () => {
        syncVariantFormAttributes();
      }
    );
    // ────────────────────────────────────────────────────────

    const loadProduct = async () => {
      if (!routeProductId.value) return;
      loading.value = true;
      try {
        const res = await productService.getProduct(routeProductId.value);
        if (res && res.success && res.data) {
          const p = res.data;
          form.name = p.name ?? '';
          form.summary = p.summary ?? '';
          form.introduction = p.introduction ?? '';
          form.specDescription = p.specDescription ?? '';
          form.returnDescription = p.returnDescription ?? '';
          form.imageUrl = p.imageUrl ?? '';
          form.categoryId = p.categoryId ?? null;
          form.productType = p.productType ?? 'standard';
          form.logisticsTemperature = p.logisticsTemperature === 'reefer' || p.logisticsTemperature === 'freeze' ? p.logisticsTemperature : 'normal';
          form.status = p.status === 'active' ? 'active' : 'inactive';
          form.storefrontSlug = p.storefrontSlug ?? p.storefront_slug ?? '';
          form.seoTitle = p.seoTitle ?? p.seo_title ?? '';
          form.seoDescription = p.seoDescription ?? p.seo_description ?? '';
          form.seoKeywords = p.seoKeywords ?? p.seo_keywords ?? '';
          const rawAttrIds = p.attributeDefinitionIds ?? p.attribute_definition_ids ?? [];
          form.attributeDefinitionIds = Array.isArray(rawAttrIds) ? [...rawAttrIds] : [];
          currentProductId.value = routeProductId.value;
        }
      } catch (err) {
        console.error(err);
        await showError('載入失敗', err.response?.data?.message || err.message);
        router.push('/client/products');
      } finally {
        loading.value = false;
      }
    };

    const loadVariants = async () => {
      if (!currentProductId.value) return;
      try {
        const res = await productService.listVariants(currentProductId.value);
        variants.value = (res && res.success && res.data && res.data.items) ? res.data.items : [];
        // 同步更新預設庫存數量（從預設規格讀取）
        const defaultVariant = variants.value.find(v => v.isDefaultVariant);
        defaultStockQuantity.value = defaultVariant ? Number(defaultVariant.stockQuantity) : null;
        // 進銷存管理模式下同步查詢各規格倉庫庫存加總
        await loadVariantStockTotals();
      } catch (e) {
        console.error(e);
      }
    };

    const loadImages = async () => {
      if (!currentProductId.value) return;
      try {
        const res = await productService.listProductImages(currentProductId.value, false);
        productImages.value = (res && res.success && res.data && res.data.items) ? res.data.items : [];
      } catch (e) {
        console.error(e);
      }
    };

    const loadCategories = async () => {
      try {
        const res = await productService.getCategoriesTree();
        // axios 攔截器已解包：res = { success, data: [...tree], message }
        categoriesTree.value = res.data || [];
      } catch (e) {
        console.error(e);
      }
    };

    // 二階分類列表（依一階選擇）
    const l2Categories = computed(() => {
      if (!selectedL1Id.value) return [];
      const lv1 = categoriesTree.value.find(c => c.id === selectedL1Id.value);
      return lv1 ? (lv1.children || []) : [];
    });

    // 三階分類列表（依二階選擇）
    const l3Categories = computed(() => {
      if (!selectedL2Id.value) return [];
      const lv2 = l2Categories.value.find(c => c.id === selectedL2Id.value);
      return lv2 ? (lv2.children || []) : [];
    });

    // 設定分類選擇（從 form.categoryId 反推各層選擇）
    const initCategorySelection = () => {
      if (!form.categoryId) { selectedL1Id.value = null; selectedL2Id.value = null; return; }
      for (const lv1 of categoriesTree.value) {
        if (lv1.id === form.categoryId) {
          selectedL1Id.value = lv1.id;
          selectedL2Id.value = null;
          return;
        }
        for (const lv2 of lv1.children || []) {
          if (lv2.id === form.categoryId) {
            selectedL1Id.value = lv1.id;
            selectedL2Id.value = lv2.id;
            return;
          }
          for (const lv3 of lv2.children || []) {
            if (lv3.id === form.categoryId) {
              selectedL1Id.value = lv1.id;
              selectedL2Id.value = lv2.id;
              return;
            }
          }
        }
      }
    };

    const onL1CategoryChange = () => {
      selectedL2Id.value = null;
      form.categoryId = selectedL1Id.value;
    };

    const onL2CategoryChange = () => {
      form.categoryId = selectedL2Id.value || selectedL1Id.value;
    };

    const syncVariantFormAttributes = () => {
      const next = {};
      productVariantAttributeDefinitions.value.forEach((d) => {
        next[d.attributeCode] = variantForm.attributes[d.attributeCode] ?? '';
      });
      variantForm.attributes = next;
    };

    const getMissingVariantAttributeNames = (attributes = variantForm.attributes) => {
      return productVariantAttributeDefinitions.value
        .filter((d) => {
          const val = attributes[d.attributeCode];
          return val === undefined || val === null || String(val).trim() === '';
        })
        .map((d) => d.attributeName);
    };

    const loadAttributeDefinitions = async () => {
      try {
        const res = await productService.getAttributeDefinitions();
        const items = (res && res.success && res.data && res.data.items) ? res.data.items : [];
        attributeDefinitions.value = items;
        syncVariantFormAttributes();
      } catch (e) {
        console.error(e);
      }
    };

    const isAttributeCheckboxDisabled = (defId) => {
      const selected = form.attributeDefinitionIds || [];
      return selected.length >= MAX_ATTRIBUTE_DEFINITIONS && !selected.includes(defId);
    };

    const buildPayload = () => ({
      name: form.name,
      summary: form.summary || null,
      introduction: form.introduction || null,
      specDescription: form.specDescription || null,
      returnDescription: form.returnDescription || null,
      productType: form.productType || 'standard',
      logisticsTemperature: form.logisticsTemperature === 'reefer' || form.logisticsTemperature === 'freeze' ? form.logisticsTemperature : 'normal',
      attributeDefinitionIds: form.productType === 'bundle' ? [] : (form.attributeDefinitionIds || []),
      imageUrl: form.imageUrl || null,
      categoryId: form.categoryId || null,
      storefrontSlug: form.storefrontSlug || null,
      seoTitle: form.seoTitle || null,
      seoDescription: form.seoDescription || null,
      seoKeywords: form.seoKeywords || null,
      status: form.status === 'active' ? 'active' : 'inactive',
      /** 指定出貨改由商城整單設定，不再使用每商品天數；儲存時清空 DB 欄位 */
      scheduledLeadDays: null,
    });

    const handleSubmit = async () => {
      if (
        isEdit.value
        && form.productType === 'bundle'
        && realVariants.value.length > 1
      ) {
        await showError('無法改為套組商品', '此商品已有多個規格，套組商品只允許一種規格，請先刪除多餘規格後再試。');
        return;
      }
      if (
        form.productType === 'standard'
        && (form.attributeDefinitionIds || []).length > MAX_ATTRIBUTE_DEFINITIONS
      ) {
        await showError('規格屬性超出上限', `最多只能選擇 ${MAX_ATTRIBUTE_DEFINITIONS} 個規格屬性，請取消多餘的勾選後再儲存。`);
        return;
      }
      submitting.value = true;
      try {
        const payload = buildPayload();
        if (currentProductId.value) {
          const res = await productService.updateProduct(currentProductId.value, payload);
          if (res && res.data && res.data.storefrontSlug) {
            form.storefrontSlug = res.data.storefrontSlug;
          }
          await showSuccess('已儲存');
        } else {
          const res = await productService.createProduct(payload);
          if (res && res.success && res.data && res.data.id) {
            currentProductId.value = res.data.id;
            if (res.data.storefrontSlug) {
              form.storefrontSlug = res.data.storefrontSlug;
            }
            await showSuccess('商品已建立，請至「規格」頁籤新增 SKU 與原價');
            await loadVariants();
            await loadImages();
            await loadAttributeDefinitions();
            activeTab.value = 'variants';
            showVariantForm.value = true;
          } else {
            await showSuccess('已建立');
            router.push('/client/products');
          }
        }
      } catch (err) {
        console.error(err);
        await showError('儲存失敗', err.response?.data?.message || err.message);
      } finally {
        submitting.value = false;
      }
    };

    const cancelVariantForm = () => {
      showVariantForm.value = false;
      editingVariantId.value = null;
      Object.assign(variantForm, defaultVariantForm());
      syncVariantFormAttributes();
    };

    const submitVariantForm = async () => {
      if (!variantForm.sku || !currentProductId.value) {
        await showError('請填寫 SKU', '規格 SKU 為必填欄位');
        return;
      }
      if (variantForm.unitPrice === null || variantForm.unitPrice === '' || variantForm.unitPrice === undefined || isNaN(Number(variantForm.unitPrice))) {
        await showError('請填寫原價', '規格原價為必填欄位');
        return;
      }
      if (!Number.isInteger(Number(variantForm.unitPrice)) || Number(variantForm.unitPrice) < 0) {
        await showError('原價格式不正確', '原價須為 0 以上的整數');
        return;
      }
      const specialRaw = variantForm.specialPrice;
      const hasSpecial = specialRaw !== null && specialRaw !== '' && specialRaw !== undefined && !Number.isNaN(Number(specialRaw));
      if (hasSpecial) {
        const specialPrice = Number(specialRaw);
        if (!Number.isInteger(specialPrice) || specialPrice <= 0) {
          await showError('特價格式不正確', '特價須為大於 0 的整數');
          return;
        }
        if (specialPrice >= Number(variantForm.unitPrice)) {
          await showError('特價須小於原價', '請調低特價，或清空特價改以原價販售');
          return;
        }
      }
      const isCreating = !editingVariantId.value;
      if (isCreating && form.productType === 'bundle' && displayVariants.value.length >= 1) {
        await showError('無法新增規格', '套組商品只允許一種規格，請直接編輯現有規格。');
        return;
      }
      if (isCreating && form.productType === 'standard' && !productVariantAttributeDefinitions.value.length && displayVariants.value.length >= 1) {
        await showError(
          '無法新增規格',
          '未勾選規格屬性時，商品只允許一種規格，請直接編輯現有規格。若要新增多種規格，請至「基本資料」勾選規格屬性。'
        );
        return;
      }
      const missingAttributeNames = getMissingVariantAttributeNames();
      if (missingAttributeNames.length > 0) {
        await showError('請填寫規格屬性', `請選擇：${missingAttributeNames.join('、')}`);
        return;
      }
      const editingDefault = editingVariantId.value && variants.value.find(v => v.id === editingVariantId.value)?.isDefaultVariant;
      // 新增真實規格前：若有預設規格（有庫存數量），提示使用者庫存將清空
      if (isCreating && !editingDefault && defaultStockQuantity.value !== null) {
        const confirmed = window.confirm(
          `新增規格後，預設規格的庫存數量（${defaultStockQuantity.value}）將被清除，\n庫存改由各規格獨立管理。\n\n確定要繼續嗎？`
        );
        if (!confirmed) return;
      }
      variantSubmitting.value = true;
      try {
        const payload = {
          sku: variantForm.sku,
          barcode: variantForm.barcode || null,
          unitPrice: Number(variantForm.unitPrice),
          specialPrice: hasSpecial ? Number(variantForm.specialPrice) : null,
          lengthCm: variantForm.lengthCm ?? null,
          widthCm: variantForm.widthCm ?? null,
          heightCm: variantForm.heightCm ?? null,
          weightKg: variantForm.weightKg ?? null,
          stockQuantity: Number(variantForm.stockQuantity) || 0,
          safetyStockQuantity: variantForm.safetyStockQuantity != null && variantForm.safetyStockQuantity !== '' ? Number(variantForm.safetyStockQuantity) : null,
          attributes: { ...variantForm.attributes },
          status: 'active',
          visibleOnStorefront: variantForm.visibleOnStorefront !== false,
        };
        Object.keys(payload.attributes).forEach(k => { if (!payload.attributes[k]) delete payload.attributes[k]; });
        if (!isCreating) {
          await productService.updateVariant(currentProductId.value, editingVariantId.value, payload);
          await showSuccess('規格已更新');
        } else {
          await productService.createVariant(currentProductId.value, payload);
          await showSuccess('規格已新增');
        }
        cancelVariantForm();
        await loadVariants();
        // 新增真實規格後，預設規格已被後端刪除，清空庫存顯示
        if (isCreating) {
          defaultStockQuantity.value = null;
        }
      } catch (err) {
        await showError('操作失敗', err.response?.data?.message || err.message);
      } finally {
        variantSubmitting.value = false;
      }
    };

    const deleteVariantRow = async (variant) => {
      if (!currentProductId.value || !variant?.id) return;
      const displayedStock = Number(formatVariantStock(variant)) || 0;
      if (displayedStock > 0) {
        await showError('無法刪除', `此規格庫存為 ${displayedStock}，請先將庫存調成 0 再刪除`);
        return;
      }
      const confirmed = await showConfirm(
        '刪除規格',
        `確定刪除規格 ${variant.sku}？刪除後無法復原。`,
        '刪除',
        '取消'
      );
      if (!confirmed) return;
      try {
        await productService.deleteVariant(currentProductId.value, variant.id);
        if (editingVariantId.value === variant.id) cancelVariantForm();
        await showSuccess('已刪除');
        await loadVariants();
        await loadVariantStockTotals();
      } catch (err) {
        await showError('刪除失敗', err.response?.data?.message || err.message);
      }
    };

    const editVariant = (v) => {
      editingVariantId.value = v.id;
      variantForm.sku = v.sku ?? '';
      variantForm.barcode = v.barcode ?? '';
      variantForm.unitPrice = v.unitPrice == null || v.unitPrice === '' ? null : Number(v.unitPrice);
      variantForm.specialPrice = v.specialPrice == null || v.specialPrice === '' ? null : Number(v.specialPrice);
      variantForm.stockQuantity = v.stockQuantity ?? 0;
      variantForm.safetyStockQuantity = v.safetyStockQuantity ?? null;
      variantForm.lengthCm = v.lengthCm ?? null;
      variantForm.widthCm = v.widthCm ?? null;
      variantForm.heightCm = v.heightCm ?? null;
      variantForm.weightKg = v.weightKg ?? null;
      variantForm.status = 'active';
      variantForm.visibleOnStorefront = v.status === 'active' && v.visibleOnStorefront !== false;
      variantForm.attributes = { ...(v.attributes || {}) };
      productVariantAttributeDefinitions.value.forEach((d) => {
        if (variantForm.attributes[d.attributeCode] === undefined) {
          variantForm.attributes[d.attributeCode] = '';
        }
      });
      syncVariantFormAttributes();
      showVariantForm.value = true;
    };

    const imageGalleryFull = computed(() => productImages.value.length >= 6);

    const assertProductImageUrl = (value) => {
      const imageUrl = value.trim();
      if (!imageUrl || imageUrl.length > 500) {
        throw new Error(imageUrl.length > 500 ? '圖片網址不能超過 500 字' : '請輸入 https 的 WebP 圖片網址');
      }
      let url;
      try {
        url = new URL(imageUrl);
      } catch {
        throw new Error('請輸入 https 的 WebP 圖片網址');
      }
      if (url.protocol !== 'https:' || !url.pathname.toLowerCase().endsWith('.webp')) {
        throw new Error('請輸入 https 的 WebP 圖片網址');
      }
    };

    const addImage = async () => {
      const imageUrl = newImageUrl.value.trim();
      if (!imageUrl || !currentProductId.value) return;
      if (imageGalleryFull.value) {
        await showError('無法新增', '圖庫最多 6 張，請先刪除再新增。');
        return;
      }
      try {
        assertProductImageUrl(imageUrl);
      } catch (err) {
        await showError('無法新增', err.message);
        return;
      }
      imageSubmitting.value = true;
      try {
        await productService.createProductImage(currentProductId.value, {
          imageUrl,
          variantId: newImageVariantId.value || null,
        });
        await showSuccess('已新增圖片');
        newImageUrl.value = '';
        newImageVariantId.value = '';
        await loadImages();
      } catch (err) {
        await showError('新增失敗', err.response?.data?.message || err.message);
      } finally {
        imageSubmitting.value = false;
      }
    };

    const deleteImage = async (img) => {
      if (!currentProductId.value) return;
      if (img.imageUrl && img.imageUrl === form.imageUrl) {
        await showError('無法刪除', '目前此張圖片為主圖，請先更換主圖後再刪除此張圖片。');
        return;
      }
      try {
        await productService.deleteProductImage(currentProductId.value, img.id);
        // 若刪除的是主圖，清除主圖設定
        if (img.imageUrl === form.imageUrl) {
          form.imageUrl = '';
        }
        await showSuccess('已刪除');
        await loadImages();
      } catch (err) {
        await showError('刪除失敗', err.response?.data?.message || err.message);
      }
    };

    const setMainImage = async (img) => {
      if (!currentProductId.value) return;
      const newUrl = img ? img.imageUrl : '';
      settingMainImage.value = true;
      try {
        await productService.updateProduct(currentProductId.value, { imageUrl: newUrl });
        form.imageUrl = newUrl;
        await showSuccess(img ? '已設為主圖' : '已取消主圖');
      } catch (err) {
        await showError('設定失敗', err.response?.data?.message || err.message);
      } finally {
        settingMainImage.value = false;
      }
    };

    /**
     * 透過 Presigned URL 直傳圖片到 S3
     * 1. 向後端取得 presigned URL + objectKey
     * 2. 直接 PUT 圖片到 S3
     * 3. 視用途：確認上傳（寫入 product_images）或單純回傳 fileUrl
     */
    const uploadViaPresignedUrl = async (file, productId, purpose) => {
      const urlRes = await productService.getUploadUrl(productId, {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        purpose,
      });
      const { presignedUrl, objectKey, fileUrl } = urlRes?.data || {};
      if (!presignedUrl || !objectKey) throw new Error('無法取得上傳 URL');

      await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      }).then(r => { if (!r.ok) throw new Error(`S3 上傳失敗 (${r.status})`); });

      return { objectKey, fileUrl };
    };

    const validateGalleryImageFile = (file) => {
      if (file.type !== 'image/webp') throw new Error(`僅支援 WebP：${file.name}`);
      if (file.size > 2 * 1024 * 1024) throw new Error(`圖片不能超過 2MB：${file.name}`);
    };

    const validateDetailImageFile = (file) => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) throw new Error(`僅支援 JPG、PNG、GIF、WebP：${file.name}`);
      if (file.size > 5 * 1024 * 1024) throw new Error(`圖片不能超過 5MB：${file.name}`);
    };

    const readFilePrefix = async (file) => new Uint8Array(await file.slice(0, 12).arrayBuffer());

    const hasAscii = (bytes, start, text) => {
      if (bytes.length < start + text.length) return false;
      for (let i = 0; i < text.length; i += 1) {
        if (bytes[start + i] !== text.charCodeAt(i)) return false;
      }
      return true;
    };

    const detectImageFormat = (bytes) => {
      if (hasAscii(bytes, 0, 'RIFF') && hasAscii(bytes, 8, 'WEBP')) return 'webp';
      if (bytes.length >= 4 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'png';
      if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
      if (hasAscii(bytes, 0, 'GIF87a') || hasAscii(bytes, 0, 'GIF89a')) return 'gif';
      return null;
    };

    const assertGalleryMagic = async (file) => {
      validateGalleryImageFile(file);
      const format = detectImageFormat(await readFilePrefix(file));
      if (format !== 'webp') throw new Error(`檔案內容不是 WebP：${file.name}`);
    };

    const assertDetailMagic = async (file) => {
      validateDetailImageFile(file);
      const format = detectImageFormat(await readFilePrefix(file));
      if (!format) throw new Error(`檔案內容不是 JPG、PNG、GIF 或 WebP：${file.name}`);
    };

    const handleMultipleImageUpload = async (event) => {
      const files = Array.from(event.target.files || []);
      if (!files.length || !currentProductId.value) {
        event.target.value = '';
        return;
      }
      const remaining = 6 - productImages.value.length;
      if (remaining <= 0) {
        await showError('無法上傳', '圖庫最多 6 張，請先刪除再新增。');
        event.target.value = '';
        return;
      }
      if (files.length > remaining) {
        await showError('無法上傳', `圖庫最多 6 張，目前還可再新增 ${remaining} 張。`);
        event.target.value = '';
        return;
      }
      try {
        for (const file of files) await assertGalleryMagic(file);
      } catch (err) {
        await showError('檔案驗證失敗', err.message);
        event.target.value = '';
        return;
      }
      batchImageUploading.value = true;
      const assignFirstAsMain = !form.imageUrl;
      let done = 0;
      let assignedMain = false;
      try {
        for (const file of files) {
          batchImageProgress.value = `${done + 1}/${files.length}`;
          const { objectKey, fileUrl } = await uploadViaPresignedUrl(file, currentProductId.value, 'gallery');
          await productService.confirmUpload(currentProductId.value, {
            objectKey,
            variantId: newImageVariantId.value || null,
          });
          if (assignFirstAsMain && done === 0 && fileUrl) {
            await productService.updateProduct(currentProductId.value, { imageUrl: fileUrl });
            form.imageUrl = fileUrl;
            assignedMain = true;
          }
          done++;
        }
        if (done > 0) {
          await showSuccess(assignedMain ? `已上傳 ${done} 張圖片，並將第一張設為主圖` : `已上傳 ${done} 張圖片`);
          await loadImages();
        }
      } catch (err) {
        console.error(err);
        await showError('上傳失敗', err.response?.data?.message || err.message);
      } finally {
        batchImageUploading.value = false;
        batchImageProgress.value = '';
        event.target.value = '';
      }
    };

    const getVariantSku = (variantId) => {
      const v = variants.value.find(x => x.id === variantId);
      return v ? v.sku : '';
    };

    const variantShownOnStorefront = (variant) => (
      variant?.status === 'active' && variant?.visibleOnStorefront !== false
    );

    const formatAttributes = (attrs) => {
      if (!attrs || typeof attrs !== 'object') return '-';
      const labelByCode = Object.fromEntries(
        attributeDefinitions.value.map((d) => [d.attributeCode, d.attributeName || d.attributeCode])
      );
      const parts = Object.entries(attrs)
        .filter(([, val]) => val)
        .map(([k, val]) => `${labelByCode[k] || k}: ${val}`);
      return parts.length ? parts.join(', ') : '-';
    };

    const formatNum = (n) => (n == null || n === '') ? '-' : Number(n).toLocaleString('zh-TW');

    /** 進銷存模式：顯示倉庫庫存加總；套組商品：依組件庫存最小可組套數；否則顯示規格 stock_quantity */
    const formatVariantStock = (variant) => {
      if (!variant) return 0;
      if (form.productType === 'bundle') {
        return bundleAvailableStock.value ?? 0;
      }
      if (inventoryManaged.value && variant.id) {
        const totals = variantStockTotals.value[variant.id];
        return totals ? (totals.totalQuantity ?? 0) : 0;
      }
      return variant.stockQuantity ?? 0;
    };

    const onImgError = (e) => { e.target.style.display = 'none'; };

    const handleMainImageFileSelect = async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // 主圖上傳需要商品 ID（編輯模式）；若尚未建立則提示先儲存
      if (!currentProductId.value) {
        await showError('請先儲存商品', '請先點「建立」儲存商品後，再上傳主圖。');
        event.target.value = '';
        return;
      }

      try {
        await assertGalleryMagic(file);
      } catch (err) {
        await showError('檔案驗證失敗', err.message);
        event.target.value = '';
        return;
      }

      mainImageUploading.value = true;
      try {
        const { objectKey, fileUrl } = await uploadViaPresignedUrl(file, currentProductId.value, 'gallery');
        await productService.confirmUpload(currentProductId.value, {
          objectKey,
          purpose: 'gallery',
          persist: false,
        });
        form.imageUrl = fileUrl;
        // 同步更新商品主圖欄位
        await productService.updateProduct(currentProductId.value, { imageUrl: fileUrl });
        await showSuccess('主圖已上傳');
      } catch (err) {
        console.error(err);
        await showError('上傳失敗', err.response?.data?.message || err.message);
      } finally {
        mainImageUploading.value = false;
        event.target.value = '';
      }
    };

    const insertQuillImage = function insertQuillImage() {
      const toolbar = this;
      if (!currentProductId.value) {
        showError('請先儲存商品', '請先點「建立」儲存商品後，再插入圖片。');
        return;
      }
      let fileInput = toolbar.container.querySelector('input.ql-image[type=file]');
      if (fileInput == null) {
        fileInput = document.createElement('input');
        fileInput.setAttribute('type', 'file');
        fileInput.setAttribute('accept', 'image/jpeg,image/jpg,image/png,image/gif,image/webp');
        fileInput.classList.add('ql-image');
        fileInput.addEventListener('change', async () => {
          const file = fileInput.files?.[0];
          fileInput.value = '';
          if (!file) return;
          try {
            await assertDetailMagic(file);
            const { objectKey, fileUrl } = await uploadViaPresignedUrl(file, currentProductId.value, 'detail');
            await productService.confirmUpload(currentProductId.value, {
              objectKey,
              purpose: 'detail',
              persist: false,
            });
            const range = toolbar.quill.getSelection(true);
            toolbar.quill.insertEmbed(range.index, 'image', fileUrl, 'user');
            toolbar.quill.setSelection(range.index + 1, 0, 'silent');
          } catch (err) {
            await showError('上傳失敗', err.response?.data?.message || err.message);
          }
        });
        toolbar.container.appendChild(fileInput);
      }
      fileInput.click();
    };

    const quillOptions = {
      theme: 'snow',
      modules: {
        toolbar: {
          container: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: [] }],
            ['link', 'image'],
            ['clean'],
          ],
          handlers: {
            image: insertQuillImage,
          },
        },
      },
    };

    /** 與後端 storefront slug 規則對齊之預覽（實際儲存仍以前端送出與後端去重為準） */
    function slugifyPreview(text) {
      if (!text || typeof text !== 'string') return '';
      return text
        .normalize('NFKC')
        .toLowerCase()
        .replace(/[\s\-–—_]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .trim();
    }

    const previewProductStorefrontSlug = computed(() => {
      const manual = (form.storefrontSlug || '').trim();
      if (manual) {
        const normalizedSlug = slugifyPreview(manual);
        if (normalizedSlug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(normalizedSlug)) return '';
        if (normalizedSlug) return normalizedSlug;
      }
      return currentProductId.value || '（儲存後為商品 ID）';
    });

    const goBack = () => router.push('/client/products');

    onMounted(async () => {
      await Promise.all([loadCategories(), loadInventorySettings(), loadAttributeDefinitions()]);
      await loadProduct();
      // 讀完 product 後，反推一階 parentId 選擇
      initCategorySelection();
      if (currentProductId.value) {
        await loadVariants(); // loadVariants 已內含 defaultStockQuantity 更新
        await loadImages();
        await loadBundleItems();
      }
    });

    return {
      isEdit,
      form,
      loading,
      submitting,
      currentProductId,
      variants,
      productImages,
      imageGalleryFull,
      attributeDefinitions,
      productVariantAttributeDefinitions,
      showVariantForm,
      variantForm,
      variantSubmitting,
      editingVariantId,
      newImageUrl,
      newImageVariantId,
      imageSubmitting,
      mainImageUploading,
      batchImageUploading,
      batchImageProgress,
      handleMainImageFileSelect,
      handleMultipleImageUpload,
      handleSubmit,
      goBack,
      cancelVariantForm,
      submitVariantForm,
      editVariant,
      deleteVariantRow,
      addImage,
      deleteImage,
      setMainImage,
      settingMainImage,
      getVariantSku,
      formatAttributes,
      variantShownOnStorefront,
      formatNum,
      formatVariantStock,
      onImgError,
      quillOptions,
      activeTab,
      categoriesTree,
      selectedL1Id,
      selectedL2Id,
      l2Categories,
      l3Categories,
      onL1CategoryChange,
      onL2CategoryChange,
      inventoryManaged,
      inventorySettings,
      variantStockTotals,
      loadVariantStockTotals,
      bundleItems,
      bundleAvailableStock,
      bundleSubmitting,
      showBundlePicker,
      onBundleItemSelect,
      removeBundleItem,
      saveBundleItems,
      formatBundleItemSku,
      // 無規格商品庫存
      defaultStockQuantity,
      realVariants,
      displayVariants,
      tableVariants,
      canAddVariant,
      defaultVariantId,
      previewProductStorefrontSlug,
      MAX_ATTRIBUTE_DEFINITIONS,
      isAttributeCheckboxDisabled,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-container {
  max-width: 1000px;
  padding-top: 88px;
}

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  margin-bottom: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 52px;
  gap: 10px;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  flex-wrap: nowrap;
  gap: 8px;
}

.header-actions .btn {
  padding: 6px 12px;
  font-size: 13px;
  white-space: nowrap;
}

.page-title {
  font-size: 20px;
  line-height: 1.3;
}

@media (max-width: 600px) {
  .page-header {
    padding: 8px 12px;
  }

  .page-container {
    padding-top: 96px;
  }
}

/* ── 進銷存分倉管理提示橫幅 ── */
.inventory-managed-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  font-size: 13px;
  color: #92400e;
  flex-wrap: wrap;
}

.inventory-managed-banner i {
  color: #d97706;
  font-size: 15px;
  flex-shrink: 0;
}

.bundle-stock-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 13px;
  color: #1e40af;
  flex-wrap: wrap;
}

.bundle-stock-hint i {
  color: #2563eb;
  font-size: 15px;
  flex-shrink: 0;
}

.banner-link {
  color: #2563eb;
  text-decoration: underline;
}

/* ── 庫存鎖定顯示（分倉管理時） ── */
.stock-locked-display {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stock-locked-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stock-locked-value {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 0 12px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  min-width: 80px;
}

.stock-locked-meta {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.btn-refresh-stock {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 14px;
  transition: color 0.15s, border-color 0.15s;
}

.btn-refresh-stock:hover {
  color: #2563eb;
  border-color: #2563eb;
}

.hint-link {
  color: #2563eb;
  text-decoration: underline;
}

.field-managed-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 500;
  color: #d97706;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 4px;
  padding: 1px 6px;
  margin-left: 6px;
  vertical-align: middle;
}

.field-hint-warn {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #d97706;
  margin-top: 4px;
}

/* ── 表頭小圖示 ── */
.th-managed-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  color: #d97706;
  font-size: 13px;
  vertical-align: middle;
}

/* 頁籤 */
.tab-bar {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 0;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  border-radius: 4px 4px 0 0;
  transition: all 0.15s;
  white-space: nowrap;
}

.tab-btn:hover {
  color: #4f80e1;
  background: #f5f8ff;
}

.tab-btn.active {
  color: #4f80e1;
  border-bottom-color: #4f80e1;
  background: none;
}

.tab-btn i {
  font-size: 16px;
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #4f80e1;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  border-radius: 9px;
}

.product-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
}

.form-section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px 0;
}

.form-row {
  margin-bottom: 16px;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-label-sub {
  font-size: 12px;
  font-weight: 500;
  color: #666;
}

.default-variant-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 500;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 4px;
  vertical-align: middle;
}

.variant-dimensions-section .grid-3 {
  margin-bottom: 0;
}

.form-row.grid-3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #333;
}

.form-label.required::after {
  content: ' *';
  color: #c00;
}

.char-count {
  font-size: 12px;
  color: #9ca3af;
  text-align: right;
  margin-top: 4px;
}

.char-count-warning {
  color: #e65100;
  font-weight: 500;
}

.field-hint {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}

.field-hint a {
  color: #3b82f6;
  text-decoration: none;
}

.attribute-checkbox-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.attribute-checkbox-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

.attribute-checkbox-item:hover {
  background: #f9fafb;
}

.attribute-checkbox-item--disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.attribute-checkbox-item--disabled:hover {
  background: transparent;
}

.attribute-selection-count {
  color: #6b7280;
  font-weight: 500;
}

.attribute-checkbox-item input {
  flex-shrink: 0;
}

.attribute-checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.attr-code-hint {
  font-size: 12px;
  color: #9ca3af;
  font-family: monospace;
}

.field-hint.field-hint-info {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  color: #2563eb;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 5px;
  padding: 5px 9px;
  line-height: 1.5;
}

.field-hint.field-hint-info i {
  font-size: 13px;
  margin-top: 1px;
  flex-shrink: 0;
}

.field-hint.field-hint-warn {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 5px;
  padding: 5px 9px;
  line-height: 1.5;
}

.field-hint.field-hint-warn i {
  font-size: 13px;
  margin-top: 1px;
  flex-shrink: 0;
  color: #d97706;
}

/* ── 無規格庫存提示 ── */
.no-variant-inventory-hint {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 16px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  font-size: 13px;
  color: #92400e;
  line-height: 1.6;
}

.no-variant-inventory-hint i {
  font-size: 18px;
  color: #d97706;
  flex-shrink: 0;
  margin-top: 1px;
}

.bundle-variant-limit-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 13px;
  color: #1e40af;
}

.bundle-variant-limit-hint i {
  font-size: 16px;
  flex-shrink: 0;
}

.stock-input {
  max-width: 160px;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.main-image-row {
  display: flex;
  flex-direction: column;
  gap: 12px;
}


.variant-toolbar {
  margin-bottom: 12px;
}

.variant-form-inline {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 16px;
}

/* 規格表單內的 form-row：覆寫全局 grid 布局，改為垂直堆疊 */
.variant-form-inline .form-row {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.variant-form-inline .form-row .form-control {
  width: 100%;
}

.variant-form-inline .form-row .field-hint {
  margin-top: 5px;
}

.variant-form-inline .form-row.actions {
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 12px;
}

.variant-table {
  overflow-x: auto;
}

.variant-table table {
  min-width: 800px;
}

.form-hint {
  font-size: 13px;
  color: #666;
  margin: -8px 0 12px 0;
}

.required-mark {
  color: #e53e3e;
  font-weight: 700;
  margin-left: 2px;
}

.form-row-block {
  display: block !important;
}

.form-row-inline {
  display: flex;
  gap: 16px;
}

.form-inline-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

@media (max-width: 600px) {
  .form-row-inline {
    flex-direction: column;
    gap: 0;
  }
}

.quill-wrapper {
  width: 100%;
}

.quill-editor {
  min-height: 240px;
}

.quill-editor :deep(.ql-container) {
  min-height: 200px;
  font-size: 14px;
}

.quill-editor :deep(.ql-editor) {
  min-height: 200px;
  line-height: 1.6;
}

.quill-editor :deep(.ql-editor p) {
  margin: 4px 0;
  line-height: 1.6;
}

.quill-editor :deep(.ql-toolbar) {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border-bottom: 1px solid #ccc;
}

.quill-editor :deep(.ql-container) {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}


.btn-upload-multi {
  margin: 0;
  cursor: pointer;
}

.btn-upload-multi.uploading {
  opacity: 0.7;
  pointer-events: none;
}

.btn-upload-multi input.sr-only {
  position: absolute;
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
}

.image-toolbar {
  margin-bottom: 12px;
}

.add-image-inline {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
}

.add-image-inline .image-url-input {
  flex: 1 1 240px;
  min-width: 180px;
  max-width: none;
  width: auto;
}

.add-image-inline .image-variant-select {
  flex: 0 0 140px;
  width: 140px;
  max-width: 140px;
}

.add-image-inline .btn {
  flex-shrink: 0;
}

.add-image-inline .image-toolbar-btn {
  box-sizing: border-box;
  height: 34px;
  padding: 0 12px;
  font-size: 13px;
  line-height: 1;
  border: 1px solid transparent;
}

.image-list {
  display: grid;
  grid-template-columns: repeat(3, 218px);
  gap: 12px;
  width: max-content;
  max-width: 100%;
}

/* 主圖 banner */
.main-image-banner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  background: #fffbea;
  border: 1px solid #f5c518;
  border-radius: 8px;
  margin-bottom: 16px;
}

.main-image-banner-thumb {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.main-image-banner-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.main-image-badge {
  font-size: 13px;
  font-weight: 600;
  color: #b8860b;
}

.main-image-badge i {
  color: #f5c518;
}

.main-image-note {
  margin: 0;
  font-size: 13px;
  color: #6c757d;
}

.no-main-image-hint {
  font-size: 13px;
  color: #999;
  padding: 10px 0;
  margin-bottom: 12px;
}

/* 圖片列表 */
.image-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.2s;
}

.image-item.is-main {
  border-color: #f5c518;
  background: #fffdf0;
}

.image-item-thumb-wrap {
  position: relative;
  display: inline-block;
}

.img-thumb {
  width: 200px;
  height: 200px;
  object-fit: cover;
  border-radius: 4px;
}

.img-main-badge {
  position: absolute;
  top: 4px;
  left: 4px;
  background: rgba(245, 197, 24, 0.9);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #fff;
}

.img-meta {
  font-size: 12px;
  color: #666;
}

.img-actions {
  display: flex;
  gap: 6px;
}

.img-actions .btn {
  width: 56px;
  height: 36px;
  padding: 0;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* ── 商品類型選擇器 ── */
.product-type-selector {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.type-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: border-color 0.2s, background 0.2s;
}

.type-option input[type="radio"] {
  display: none;
}

.status-radio-group {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.status-radio-group .form-check-inline {
  margin-right: 0;
}

.type-option.active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
}

.type-option i {
  font-size: 18px;
}

/* ── 套組組件 ── */
.bundle-header {
  margin-bottom: 16px;
}

.bundle-hint {
  font-size: 13px;
  color: #666;
  margin: 4px 0 0;
}

.bundle-add-row {
  margin-bottom: 16px;
}

.bundle-items-list {
  margin-bottom: 20px;
  overflow-x: auto;
}

.bundle-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.bundle-table th {
  background: #f8f9fa;
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
}

.bundle-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

.sku-cell {
  font-family: monospace;
  font-size: 13px;
  color: #555;
}

.qty-input {
  width: 70px;
  text-align: center;
  padding: 4px 8px;
}

.variant-attrs {
  color: #888;
  font-size: 12px;
}

.text-muted {
  color: #aaa;
}
</style>
